import removeMarkdown from '@noticeable/remove-markdown';

/**
 * Utility functions for converting markdown to plain text
 */

/**
 * Convert markdown text to plain text by removing markdown formatting
 * @param markdown - The markdown text to convert
 * @returns Plain text without markdown formatting
 */
export const markdownToPlainText = (markdown: string): string => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  // Use the removeMarkdown function from @noticeable/remove-markdown
  const plainText = removeMarkdown(markdown);
  
  // Clean up extra whitespace
  return plainText
    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
    .replace(/[ \t]{2,}/g, ' ') // Replace multiple spaces/tabs with single space
    .trim();
};

/**
 * Convert markdown to plain text with basic formatting preserved
 * This version keeps some structure like line breaks and basic punctuation
 * @param markdown - The markdown text to convert
 * @returns Plain text with basic formatting preserved
 */
export const markdownToFormattedText = (markdown: string): string => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  // Use removeMarkdown with options to preserve some formatting
  const plainText = removeMarkdown(markdown, {
    stripListLeaders: false, // Keep list formatting
    gfm: true, // Support GitHub Flavored Markdown
    useImgAltText: true // Use alt text for images
  });
  
  // Clean up whitespace but preserve structure
  return plainText
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

/**
 * Extract plain text content from markdown while preserving paragraph structure
 * @param markdown - The markdown text to convert
 * @returns Plain text with paragraph breaks preserved
 */
export const markdownToParagraphs = (markdown: string): string[] => {
  const plainText = markdownToPlainText(markdown);
  return plainText
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0);
};

/**
 * Get a summary of markdown content (first paragraph or specified length)
 * @param markdown - The markdown text to summarize
 * @param maxLength - Maximum length of the summary (default: 200)
 * @returns Summary text
 */
export const getMarkdownSummary = (markdown: string, maxLength: number = 200): string => {
  const plainText = markdownToPlainText(markdown);
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  // Try to break at sentence end
  const truncated = plainText.substring(0, maxLength);
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf('.'),
    truncated.lastIndexOf('!'),
    truncated.lastIndexOf('?')
  );
  
  if (lastSentenceEnd > maxLength * 0.7) {
    return truncated.substring(0, lastSentenceEnd + 1);
  }
  
  // Break at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};